import type { AxiosError, AxiosResponse } from 'axios';

import api from '@/utils/axiosInterceptor';
import type { ApiResponse } from '@/utils/deepmerge';

import type {
  IHandRequestInitialData,
  IItemDataResponse,
  SaveRequestDto,
  UpdateRequestDto,
} from './HandRequest.interface';

export async function GetInitialDataForHandRequestApi() {
  const response: AxiosResponse<ApiResponse<IHandRequestInitialData>> =
    await api.get(`/purchases-requests/get-initial-data`);
  return response?.data;
}

interface IDebtResponse {
  debt: number;
}

export async function GetSupplierDebtApi(clientId: number) {
  const response = await api.get<ApiResponse<IDebtResponse>>(
    `/clients/${clientId}/debt`,
  );
  return response?.data;
}

export async function GetItemDataApi(itemId: number, supplierId: number) {
  const response = await api.get<ApiResponse<IItemDataResponse>>(
    `/purchases-requests/items/${itemId}/${supplierId}/data`,
  );
  return response?.data;
}

export interface CreateRequestResponse {
  success: boolean;
  message: string;
  requestId?: string;
  total?: number;
  itemsCount?: number;
  status?: 'Pending Approval' | 'Pending';
}

export async function createPurchaseRequest(
  data: SaveRequestDto,
): Promise<CreateRequestResponse> {
  try {
    const response = await api.post<
      ApiResponse<{
        requestId: string;
        total: number;
        itemsCount: number;
        status: 'Pending Approval' | 'Pending';
      }>
    >('/purchases-requests/save-request', data);

    // Check for successful response
    const isSuccess =
      response.data.statusCode >= 200 && response.data.statusCode < 300;

    // Get message or provide default message
    let message = 'تم إنشاء الطلب بنجاح';
    if (response.data.messages && response.data.messages.length > 0) {
      message = response.data.messages[0].message;
    }

    return {
      success: isSuccess,
      message: message,
      requestId: response.data.data?.requestId,
      total: response.data.data?.total,
      itemsCount: response.data.data?.itemsCount,
      status: response.data.data?.status,
    };
  } catch (error) {
    console.error('API Error:', error);

    // Handle different types of errors
    let errorMessage = 'حدث خطأ أثناء إنشاء الطلب';

    // Extract error message from response if possible
    if (isAxiosError(error)) {
      if (error.response) {
        // The request was made and the server responded with an error status
        const errorData = error.response.data as {
          messages?: Array<{ message: string }>;
        };

        if (errorData?.messages && errorData.messages.length > 0) {
          errorMessage = errorData.messages[0].message;
        } else if (error.response.status === 403) {
          errorMessage = 'ليس لديك صلاحية لإنشاء طلب شراء';
        } else if (error.response.status === 400) {
          errorMessage = 'بيانات الطلب غير صحيحة';
        } else if (error.response.status === 500) {
          errorMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage =
          'لا يمكن الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت الخاص بك';
      }
    } else if (error instanceof Error) {
      // Something happened in setting up the request
      errorMessage = error.message || 'حدث خطأ أثناء إنشاء الطلب';
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
}

export async function updatePurchaseRequest(
  data: UpdateRequestDto,
): Promise<CreateRequestResponse> {
  try {
    const response = await api.put<
      ApiResponse<{ requestId: string; total: number; itemsCount: number }>
    >(`/purchases-requests/update-request`, data);

    // Check for successful response
    const isSuccess =
      response.data.statusCode >= 200 && response.data.statusCode < 300;

    // Get message or provide default message
    let message = 'تم تعديل الطلب بنجاح';
    if (response.data.messages && response.data.messages.length > 0) {
      message = response.data.messages[0].message;
    }

    return {
      success: isSuccess,
      message: message,
      requestId: response.data.data?.requestId,
      total: response.data.data?.total,
      itemsCount: response.data.data?.itemsCount,
    };
  } catch (error) {
    console.error('API Error:', error);

    // Handle different types of errors
    let errorMessage = 'حدث خطأ أثناء تعديل الطلب';

    // Extract error message from response if possible
    if (isAxiosError(error)) {
      if (error.response) {
        // The request was made and the server responded with an error status
        const errorData = error.response.data as {
          messages?: Array<{ message: string }>;
        };

        if (errorData?.messages && errorData.messages.length > 0) {
          errorMessage = errorData.messages[0].message;
        } else if (error.response.status === 403) {
          errorMessage = 'ليس لديك صلاحية لتعديل طلب الشراء';
        } else if (error.response.status === 400) {
          errorMessage = 'بيانات الطلب غير صحيحة';
        } else if (error.response.status === 404) {
          errorMessage = 'الطلب غير موجود أو تم حذفه';
        } else if (error.response.status === 500) {
          errorMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage =
          'لا يمكن الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت الخاص بك';
      }
    } else if (error instanceof Error) {
      // Something happened in setting up the request
      errorMessage = error.message || 'حدث خطأ أثناء تعديل الطلب';
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
}

function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}
